import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { API_URL } from "../config";
import "./Certificar.css";

function ImageDataRows({ record }) {
  if (!record) return null;
  const fmt = record.width > record.height ? "16:9" : record.width === record.height ? "1:1" : "9:16";
  const rows = [
    ["ID", record.id || "—"],
    ["Autor", record.author || "—"],
    ["Motor", record.motor || "—"],
    ["Modelo", (record.model_ckpt || "—").replace(".safetensors", "")],
    ["Seed", record.seed ?? "—"],
    ["Formato", fmt],
    ["Dim", record.width && record.height ? `${record.width}x${record.height}` : "—"],
    ["Creada", record.created_at ? new Date(record.created_at + "Z").toLocaleString() : "—"],
    ["Prompt", record.prompt || "—"],
  ];
  return (
    <div className="cert-data-rows">
      {rows.map(([k, v]) => (
        <div className="cert-data-row" key={k}>
          <span className="cert-data-key">{k}</span>
          <span className="cert-data-value">{String(v)}</span>
        </div>
      ))}
    </div>
  );
}

function CertDataRows({ certData, record }) {
  if (!certData?.certified) return null;
  const fmt = record?.width > record?.height ? "16:9" : record?.width === record?.height ? "1:1" : "9:16";
  const rows = [
    ["ID", certData.ividi_id || record?.id || "—"],
    ["Certificado por", certData.certified_by || "—"],
    ["Fecha cert.", certData.certified_at ? new Date(certData.certified_at + "Z").toLocaleString() : "—"],
    ["Overlay", certData.overlay ? "Si" : "No"],
    ["Autor", record?.author || "—"],
    ["Motor", record?.motor || "—"],
    ["Modelo", (record?.model_ckpt || "—").replace(".safetensors", "")],
    ["Seed", record?.seed ?? "—"],
    ["Formato", fmt],
    ["Dim", record?.width && record?.height ? `${record.width}x${record.height}` : "—"],
    ["Creada", record?.created_at ? new Date(record.created_at + "Z").toLocaleString() : "—"],
    ["Prompt", record?.prompt || "—"],
    ["Resumen", certData.summary || "—"],
  ];
  return (
    <div className="cert-data-rows">
      {rows.map(([k, v]) => (
        <div className="cert-data-row" key={k}>
          <span className="cert-data-key">{k}</span>
          <span className="cert-data-value">{String(v)}</span>
        </div>
      ))}
    </div>
  );
}

export default function Certificar({ token }) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const [allImages, setAllImages] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [record, setRecord] = useState(null);
  const [certData, setCertData] = useState(null);
  const [certifying, setCertifying] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);
  const [certTimestamp, setCertTimestamp] = useState(null);
  const [withOverlay, setWithOverlay] = useState(true);
  const [showOverlay, setShowOverlay] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Cargar lista de imagenes
  useEffect(() => {
    fetch(`${API_URL}/images?per_page=250`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setAllImages(data.items || []); })
      .catch(() => {});
  }, [token, location.key]);

  // Funcion central: seleccionar imagen y resetear todo
  const selectImage = async (id, preloadedRecord) => {
    // Siempre limpiar las 4 cajas
    setSelectedId(id);
    setLocalPreview(null);
    setCertData(null);
    setCertTimestamp(null);
    setShowOverlay(false);

    if (!id) {
      setRecord(null);
      return;
    }

    if (preloadedRecord) {
      // Upload: record ya viene del servidor
      setRecord(preloadedRecord);
    } else {
      // Selector/URL: fetch del servidor
      setRecord(null);
      try {
        const [recRes, certRes] = await Promise.all([
          fetch(`${API_URL}/images/${encodeURIComponent(id)}`, { headers }),
          fetch(`${API_URL}/images/${encodeURIComponent(id)}/certificate`, { headers }),
        ]);
        if (recRes.ok) setRecord(await recRes.json());
        if (certRes.ok) {
          const cert = await certRes.json();
          setCertData(cert);
          if (cert.certified) {
            setCertTimestamp(Date.now());
            setShowOverlay(cert.overlay === true);
          }
        }
      } catch { }
    }
  };

  // Cargar imagen inicial desde URL param
  const initialId = searchParams.get("id") || "";
  const didInit = useRef(false);
  useEffect(() => {
    if (!didInit.current && initialId) {
      didInit.current = true;
      selectImage(initialId);
    }
  }, [initialId]);

  // Certificar
  const handleCertify = async () => {
    if (!selectedId || certifying) return;
    setCertifying(true);
    try {
      const res = await fetch(`${API_URL}/images/${encodeURIComponent(selectedId)}/certify?overlay=${withOverlay}`, {
        method: "POST",
        headers,
      });
      if (res.ok) {
        const data = await res.json();
        setCertData(data);
        setCertTimestamp(Date.now());
        setShowOverlay(withOverlay);
      }
    } finally {
      setCertifying(false);
    }
  };

  // Descargar como archivo (blob para forzar Save As)
  const handleDownload = async () => {
    if (!selectedId || !certData?.certified) return;
    try {
      const res = await fetch(`${API_URL}/images/${encodeURIComponent(selectedId)}/file/certified`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(selectedId || "cert").replace("#", "")}_certified.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { }
  };

  // Drag & drop
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const uploadFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setLocalPreview(URL.createObjectURL(file));
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await fetch(`${API_URL}/images/upload`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (res.ok) {
        const data = await res.json();
        setAllImages(prev => [data, ...prev]);
        selectImage(data.id, data);
      }
    } catch { }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFile(e.dataTransfer?.files?.[0]);
  };

  const handleFileSelect = () => fileInputRef.current?.click();
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = "";
  };

  // Selector change
  const handleSelectorChange = (e) => {
    selectImage(e.target.value);
  };

  const isCertified = certData?.certified === true;
  const originalSrc = localPreview || (selectedId ? `${API_URL}/images/${encodeURIComponent(selectedId)}/file` : null);
  const certifiedSrc = isCertified && certTimestamp ? `${API_URL}/images/${encodeURIComponent(selectedId)}/file/certified?t=${certTimestamp}` : null;

  return (
    <div className="cert-root">
      <div className="cert-toolbar">
        <h2 className="cert-title">Certificar iVidi</h2>
        <select
          className="cert-selector"
          value={selectedId}
          onChange={handleSelectorChange}
        >
          <option value="">Seleccionar imagen...</option>
          {allImages.map(img => (
            <option key={img.id} value={img.id}>
              {img.id} — {img.author} — {(img.model_ckpt || "").replace(".safetensors", "")}
            </option>
          ))}
        </select>
      </div>

      <div className="cert-columns">
        {/* Columna izquierda */}
        <div
          className="cert-col"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="cert-col-label">Imagen Original</div>
          <div className={`cert-img-box ${dragOver ? "cert-drag-over" : ""}`}>
            {originalSrc ? (
              <img src={originalSrc} alt="Original" />
            ) : (
              <div className="cert-placeholder">Arrastra una imagen o selecciona del desplegable</div>
            )}
          </div>
          <div className="cert-col-actions">
            <button className="cert-btn-upload" onClick={handleFileSelect}>Cargar imagen</button>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
          </div>
          <div className="cert-data-label">Certificado Imagen Original</div>
          <div className="cert-data-box">
            {record ? (
              <ImageDataRows record={record} />
            ) : selectedId ? (
              <span className="cert-no-cert">Cargando...</span>
            ) : (
              <span className="cert-no-cert">Sin imagen seleccionada</span>
            )}
          </div>
        </div>

        {/* Columna central */}
        <div className="cert-center">
          <button
            className="cert-btn-certify"
            disabled={!selectedId || isCertified || certifying}
            onClick={handleCertify}
          >
            {certifying ? "Certificando..." : isCertified ? "Ya certificada" : "Certificar iVidi"}
          </button>
          <label className="cert-overlay-check">
            <input
              type="checkbox"
              checked={withOverlay}
              onChange={(e) => setWithOverlay(e.target.checked)}
              disabled={isCertified}
            />
            Certificado impreso
          </label>
        </div>

        {/* Columna derecha */}
        <div className="cert-col">
          <div className="cert-col-label">Imagen Certificada iVidi</div>
          <div className="cert-img-box cert-img-box-right">
            {certifiedSrc ? (
              <>
                <img src={certifiedSrc} alt="Certificada" />
                {showOverlay && !certData?.overlay && certData?.summary && (
                  <div className="cert-css-overlay">{certData.summary}</div>
                )}
              </>
            ) : (
              <div className="cert-placeholder">
                {selectedId ? "Haz clic en Certificar para generar el certificado" : ""}
              </div>
            )}
          </div>
          <div className="cert-col-actions">
            <button
              className="cert-btn-download"
              disabled={!isCertified}
              onClick={handleDownload}
            >
              Descargar Certificada
            </button>
            {isCertified && (
              <label className="cert-overlay-check">
                <input
                  type="checkbox"
                  checked={showOverlay}
                  onChange={(e) => setShowOverlay(e.target.checked)}
                  disabled={certData?.overlay === true}
                />
                Ver certificado impreso
              </label>
            )}
          </div>
          <div className="cert-data-label">Certificado Imagen Certificada iVidi</div>
          <div className="cert-data-box">
            {isCertified ? (
              <CertDataRows certData={certData} record={record} />
            ) : (
              <span className="cert-no-cert">Imagen sin certificado iVidi</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
